import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatPrice } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, Plus, Eye, EyeOff, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";

export default function OwnerProducts() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const load = () => {
    setLoading(true);
    api.get("/admin/products", { params: { q, page, limit: 30 } })
      .then(({ data }) => { setItems(data.items); setTotal(data.total); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);

  const toggle = async (pid) => {
    try {
      await api.post(`/admin/products/${pid}/toggle-published`);
      toast.success("Publish state updated.");
      load();
    } catch { toast.error("Could not update."); }
  };

  return (
    <div data-testid="owner-products">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-slate-400">Catalog</div>
          <h1 className="mt-1 font-display text-3xl font-extrabold text-white">Products <span className="text-slate-500 text-lg font-mono">({total})</span></h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search title / SKU / item#" data-testid="admin-product-search" className="pl-10 w-72 bg-white/5 border-white/10 text-white" />
          </div>
          <Button asChild data-testid="admin-add-product-btn" className="bg-[#0055FF] hover:bg-[#0044CC] gap-1.5">
            <Link to="/owner-cabinet/products/new"><Plus className="w-4 h-4" /> New product</Link>
          </Button>
        </div>
      </div>

      {loading ? (
        <Skeleton className="h-96 bg-white/5" />
      ) : (
        <div className="rounded-xl border border-white/10 bg-white/5 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest font-mono text-slate-400 border-b border-white/10">
                <th className="p-3">Product</th>
                <th className="p-3">SKU</th>
                <th className="p-3">Price</th>
                <th className="p-3">Stock</th>
                <th className="p-3">Status</th>
                <th className="p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.product_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white rounded overflow-hidden shrink-0"><img src={p.main_image} alt="" className="w-full h-full object-contain" /></div>
                      <Link to={`/owner-cabinet/products/${p.product_id}`} className="text-white hover:text-[#0055FF] line-clamp-2 max-w-md">{p.title}</Link>
                    </div>
                  </td>
                  <td className="p-3 font-mono text-slate-300 text-xs">{p.sku}</td>
                  <td className="p-3 text-white">{formatPrice(p.price)}</td>
                  <td className="p-3 text-slate-300 font-mono text-xs">{p.stock}</td>
                  <td className="p-3">
                    {p.published ? <Badge className="bg-emerald-600 text-white">Live</Badge> : <Badge variant="secondary">Draft</Badge>}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="ghost" onClick={() => toggle(p.product_id)} data-testid={`toggle-${p.product_id}`} className="text-slate-300 hover:text-white h-8">
                        {p.published ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </Button>
                      {p.ebay_url && (
                        <a href={p.ebay_url} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-[#0055FF]"><ExternalLink className="w-3.5 h-3.5" /></a>
                      )}
                      <Button asChild size="sm" variant="outline" className="h-7 text-xs border-white/10 bg-white/5 text-white hover:bg-white/10">
                        <Link to={`/owner-cabinet/products/${p.product_id}`}>Edit</Link>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-6 flex items-center justify-center gap-2">
        <Button variant="outline" size="sm" className="border-white/10 bg-white/5 text-white" disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</Button>
        <div className="text-xs font-mono text-slate-400">Page {page}</div>
        <Button variant="outline" size="sm" className="border-white/10 bg-white/5 text-white" disabled={items.length < 30} onClick={() => setPage(page + 1)}>Next</Button>
      </div>
    </div>
  );
}
